import { RouterProvider } from '@tanstack/react-router'
import { useSyncExternalStore } from 'react'
import {
  getLatestSentryEventId,
  router,
  subscribeToSentryEventId,
} from './sentry'
import {
  RfhGlobalStyleProvider,
  RfhThemeProvider,
  brandOrange,
  brandPurple,
} from '@floriday/floriday-ui-style-rfh'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './services/queryClient'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

import {
  DateLibraryIntegration,
  ErrorDialog,
  SnackbarSingleton,
} from '@floriday/floriday-ui'
import { Security, useOktaAuth } from '@okta/okta-react'
import { oktaAuth as oktaInstance } from './utils/okta'
import type OktaAuth from '@okta/okta-auth-js'
import { toRelativeUrl } from '@okta/okta-auth-js'
import { CollectingFormProvider } from './context/collectingContext'
import { UserProvider } from './context/userContext'

DateLibraryIntegration.configure({
  nativeDateToLibraryDate: (date: Date) => date,
})

function RouterWithContext() {
  const auth = useOktaAuth()
  return <RouterProvider router={router} context={{ auth }} />
}

const SentryErrorDialog = () => {
  const sentryEventId = useSyncExternalStore(
    subscribeToSentryEventId,
    getLatestSentryEventId,
    getLatestSentryEventId
  )

  return (
    <ErrorDialog
      unexpectedErrorMessage='Er is een melding gemaakt bij het development team'
      sessionId={sentryEventId}
    />
  )
}

const App = () => {
  return (
    // Both collecting and returning contexts share this same underlying provider
    <CollectingFormProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <RfhThemeProvider primary={brandOrange} secondary={brandPurple}>
          <RfhGlobalStyleProvider>
            <QueryClientProvider client={queryClient}>
              <Security
                oktaAuth={oktaInstance}
                restoreOriginalUri={(
                  _oktaAuth: OktaAuth,
                  originalUri = '/'
                ) => {
                  router.history.push(
                    toRelativeUrl(originalUri, window.location.origin)
                  )
                }}
              >
                <UserProvider>
                  <RouterWithContext />
                  <SentryErrorDialog />
                  <SnackbarSingleton
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                  />
                </UserProvider>
              </Security>
            </QueryClientProvider>
          </RfhGlobalStyleProvider>
        </RfhThemeProvider>
      </LocalizationProvider>
    </CollectingFormProvider>
  )
}

export default App
